import { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog } from 'radix-ui';
import { GripVertical, ChevronUp, ChevronDown, Save, X, ListOrdered } from 'lucide-react';
import { api } from '../services/api';

interface Props {
  open: boolean;
  onClose: () => void;
  categories: string[];
  initialOrder: string[];
  onSaved: (order: string[]) => void;
}

function mergeOrder(savedOrder: string[], present: string[]): string[] {
  const presentSet = new Set(present);
  const knownInOrder = savedOrder.filter(c => presentSet.has(c));
  const known = new Set(knownInOrder);
  const newOnes = present.filter(c => !known.has(c)).sort((a, b) => a.localeCompare(b));
  return [...knownInOrder, ...newOnes];
}

export function CategoryOrderDialog({ open, onClose, categories, initialOrder, onSaved }: Props) {
  const merged = useMemo(() => mergeOrder(initialOrder, categories), [initialOrder, categories]);
  const [order, setOrder] = useState<string[]>(merged);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dragIndex = useRef<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setOrder(merged);
      setError('');
    }
  }, [open, merged]);

  const move = (from: number, to: number) => {
    if (to < 0 || to >= order.length || from === to) return;
    setOrder(prev => {
      const next = prev.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  const onDragStart = (i: number) => (e: React.DragEvent) => {
    dragIndex.current = i;
    e.dataTransfer.effectAllowed = 'move';
    try { e.dataTransfer.setData('text/plain', String(i)); } catch {}
  };
  const onDragOver = (i: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (overIndex !== i) setOverIndex(i);
  };
  const onDrop = (i: number) => (e: React.DragEvent) => {
    e.preventDefault();
    const from = dragIndex.current;
    dragIndex.current = null;
    setOverIndex(null);
    if (from == null) return;
    move(from, i);
  };
  const onDragEnd = () => {
    dragIndex.current = null;
    setOverIndex(null);
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    try {
      const result = await api.updateCategoryOrder(order);
      onSaved(result.categoryOrder);
      onClose();
    } catch (err: any) {
      setError(err.message || 'Failed to save order');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <Dialog.Content className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-[min(520px,calc(100vw-2rem))] max-h-[85vh] overflow-hidden flex flex-col bg-white rounded-[2rem] shadow-2xl p-7 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 bg-blue-50 border border-blue-100 rounded-2xl flex items-center justify-center">
              <ListOrdered className="w-5 h-5 text-[#2563eb]" />
            </div>
            <Dialog.Title className="text-2xl font-extrabold text-slate-900 tracking-tight">
              Reorder Categories
            </Dialog.Title>
          </div>
          <Dialog.Description className="text-slate-500 text-sm font-medium mb-5 ml-[3.25rem]">
            Drag a row or use the arrows. Products in the first category appear first on the POS.
          </Dialog.Description>

          {error && (
            <div className="px-4 py-3 bg-red-50 text-red-600 border border-red-100 rounded-2xl mb-4 text-sm font-medium">
              {error}
            </div>
          )}

          {order.length === 0 ? (
            <div className="bg-slate-50 border border-slate-200 rounded-2xl p-8 text-center text-slate-500 font-medium">
              No categories yet. Add a category to a product first.
            </div>
          ) : (
            <ul className="flex-1 overflow-y-auto space-y-2 -mx-1 px-1">
              {order.map((category, i) => (
                <li
                  key={category}
                  draggable
                  onDragStart={onDragStart(i)}
                  onDragOver={onDragOver(i)}
                  onDrop={onDrop(i)}
                  onDragEnd={onDragEnd}
                  className={`flex items-center gap-3 p-3 bg-white border rounded-2xl transition-all ${
                    overIndex === i ? 'border-[#2563eb] bg-blue-50/40 shadow-sm' : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <button
                    type="button"
                    aria-label="Drag handle"
                    className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 px-1"
                  >
                    <GripVertical className="w-5 h-5" />
                  </button>
                  <span className="flex items-center justify-center w-7 h-7 text-[11px] font-bold text-slate-500 bg-slate-100 rounded-lg">
                    {i + 1}
                  </span>
                  <span className="flex-1 font-semibold text-slate-800 truncate">{category}</span>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => move(i, i - 1)}
                      disabled={i === 0}
                      aria-label={`Move ${category} up`}
                      className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-800 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronUp className="w-4 h-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => move(i, i + 1)}
                      disabled={i === order.length - 1}
                      aria-label={`Move ${category} down`}
                      className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 border border-slate-200 text-slate-500 hover:bg-slate-100 hover:text-slate-800 active:scale-95 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <ChevronDown className="w-4 h-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="flex-1 h-12 px-4 bg-slate-100 text-slate-600 rounded-2xl font-bold hover:bg-slate-200 transition-all active:scale-[0.98] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving || order.length === 0}
              className="flex-1 h-12 px-4 bg-[#2563eb] text-white rounded-2xl font-bold hover:bg-[#1d4ed8] transition-all active:scale-[0.98] shadow-lg shadow-blue-900/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving…' : 'Save Order'}
            </button>
          </div>

          <Dialog.Close asChild>
            <button
              type="button"
              aria-label="Close"
              className="absolute top-5 right-5 w-9 h-9 rounded-full flex items-center justify-center hover:bg-slate-100 active:scale-95 transition-all"
            >
              <X className="w-5 h-5 text-slate-400" />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
